import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { 
  ChevronLeft, 
  Loader2, 
  MapPin,
  Image,
  Plus,
  X,
  Search,
  IdCard,
  Upload,
  Camera,
  Shield,
  Check
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { API } from "../utils/api";
import { trackLead } from "../utils/metaPixel";
import { useStripeConnectStatus } from "../hooks/useStripeConnect";
import StripeConnectGate from "../components/StripeConnectGate";

const FEATURE_OPTIONS = [
  "Cubierta",
  "Segura",
  "24h",
  "Vigilada",
  "Cámaras",
  "Cargador EV",
  "Fácil acceso",
  "Portero",
  "Mando",
  "Residencial",
  "Céntrica",
  "Premium"
];

const VEHICLE_TYPES = [
  { id: "car", labelKey: "vehicle.car" },
  { id: "motorcycle", labelKey: "vehicle.motorcycle" },
  { id: "electric", labelKey: "vehicle.electric" },
  { id: "van", labelKey: "vehicle.van" }
];

export default function AddSpot() {
  const navigate = useNavigate();
  const { user, token, loading: authLoading } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [addressQuery, setAddressQuery] = useState("");
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [searchingAddress, setSearchingAddress] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const addressInputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    address: "",
    city: "",
    latitude: 40.4168,
    longitude: -3.7038,
    price_hour: "",
    price_day: "",
    price_month: "",
    features: [],
    images: [],
    vehicle_types: ["car"],
    width: "",
    length: "",
    requires_dni: false,
    has_plazafy_gate: false
  });
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // Cerrar sugerencias al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target) &&
          addressInputRef.current && !addressInputRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Buscar direcciones
  useEffect(() => {
    const searchAddresses = async () => {
      if (addressQuery.length < 3) {
        setAddressSuggestions([]);
        return;
      }

      setSearchingAddress(true);
      try {
        const response = await axios.get(`${API}/address/search?q=${encodeURIComponent(addressQuery)}`);
        setAddressSuggestions(response.data.results || []);
        setShowSuggestions(true);
      } catch (error) {
        console.error("Error searching address:", error);
      } finally {
        setSearchingAddress(false);
      }
    };

    const timeoutId = setTimeout(searchAddresses, 300);
    return () => clearTimeout(timeoutId);
  }, [addressQuery]);

  if (!authLoading && !user) {
    navigate("/");
    return null;
  }

  // Stripe Connect gate: bloqueamos publicación si el host no tiene cobros activos
  // (admin queda exento)
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { status: stripeStatus, loading: stripeLoading } = useStripeConnectStatus(token);
  const isAdmin = user?.role === "admin";
  if (!isAdmin && !stripeLoading && stripeStatus && !stripeStatus.charges_enabled) {
    return <StripeConnectGate status={stripeStatus} />;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFeatureToggle = (feature) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.includes(feature)
        ? prev.features.filter(f => f !== feature)
        : [...prev.features, feature]
    }));
  };

  const handleVehicleTypeToggle = (type) => {
    setFormData(prev => ({
      ...prev,
      vehicle_types: prev.vehicle_types.includes(type)
        ? prev.vehicle_types.filter(t => t !== type)
        : [...prev.vehicle_types, type]
    }));
  };

  const handleSelectAddress = (suggestion) => {
    setFormData(prev => ({
      ...prev,
      address: suggestion.street || suggestion.address,
      city: suggestion.city,
      latitude: suggestion.latitude,
      longitude: suggestion.longitude
    }));
    setAddressQuery(suggestion.street || suggestion.address);
    setShowSuggestions(false);
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingImage(true);

    for (const file of files) {
      // Validar tamaño (10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} es demasiado grande. Máximo 10MB`);
        continue;
      }

      // Validar tipo
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} no es una imagen válida`);
        continue;
      }

      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);

        const res = await axios.post(`${API}/spots/upload-image`, formDataUpload, {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`
          }
        });

        if (res.data.url) {
          const fullUrl = `${window.location.origin}${res.data.url}`;
          setFormData(prev => ({
            ...prev,
            images: [...prev.images, fullUrl]
          }));
          toast.success("Imagen subida correctamente");
        }
      } catch (error) {
        console.error("Error uploading image:", error);
        toast.error(`Error al subir ${file.name}`);
      }
    }

    setUploadingImage(false);
    // Limpiar el input para poder subir la misma imagen de nuevo
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeImage = (url) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter(img => img !== url)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.address || !formData.city || !formData.price_hour) {
      toast.error(t('common.fillRequired') || "Por favor completa los campos requeridos");
      return;
    }

    if (!acceptedTerms) {
      toast.error("Debes aceptar los Términos y Condiciones para publicar una plaza");
      return;
    }

    setLoading(true);
    try {
      const spotData = {
        ...formData,
        price_hour: parseFloat(formData.price_hour),
        price_day: formData.price_day ? parseFloat(formData.price_day) : null,
        price_month: formData.price_month ? parseFloat(formData.price_month) : null,
        width: formData.width ? parseFloat(formData.width) : null,
        length: formData.length ? parseFloat(formData.length) : null,
        images: formData.images.length > 0 ? formData.images : ["https://images.unsplash.com/photo-1548343361-02248be15911?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"]
      };

      await axios.post(`${API}/spots`, spotData);
      trackLead('add_spot');
      toast.success(t('addSpot.success') || "¡Plaza publicada con éxito!");
      navigate("/my-spots");
    } catch (error) {
      const message = error.response?.data?.detail || (t('addSpot.error') || "Error al publicar la plaza");
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1a1625] py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="text-gray-400 hover:text-white"
          >
            <ChevronLeft className="w-6 h-6" />
          </Button>
          <h1 className="text-2xl font-bold text-white">{t('addSpot.title')}</h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Información básica */}
          <section className="bg-[#231f30] rounded-xl p-6 border border-white/5">
            <h2 className="text-lg font-semibold text-white mb-4">{t('addSpot.basicInfo')}</h2>
            
            <div className="space-y-4">
              <div>
                <Label className="text-gray-300">{t('addSpot.spotTitle')} *</Label>
                <Input
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder={t('addSpot.spotTitlePlaceholder')}
                  className="mt-1 bg-[#15121e] border-white/10 text-white"
                  required
                />
              </div>

              <div>
                <Label className="text-gray-300">{t('addSpot.description')}</Label>
                <Textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder={t('addSpot.descriptionPlaceholder')}
                  className="mt-1 bg-[#15121e] border-white/10 text-white"
                  rows={4}
                />
              </div>
            </div>
          </section>

          {/* Ubicación */}
          <section className="bg-[#231f30] rounded-xl p-6 border border-white/5">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-violet-400" />
              {t('addSpot.location')}
            </h2>
            
            <div className="space-y-4">
              <div className="relative">
                <Label className="text-gray-300">{t('addSpot.address')} *</Label>
                <div className="relative mt-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    ref={addressInputRef}
                    value={addressQuery}
                    onChange={(e) => setAddressQuery(e.target.value)}
                    placeholder={t('addSpot.addressPlaceholder')}
                    className="pl-10 bg-[#15121e] border-white/10 text-white"
                    required
                  />
                  {searchingAddress && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-violet-400 animate-spin" />
                  )}
                </div>
                
                {/* Sugerencias de direcciones */}
                {showSuggestions && addressSuggestions.length > 0 && (
                  <div 
                    ref={suggestionsRef}
                    className="absolute z-50 w-full mt-1 bg-[#231f30] border border-white/10 rounded-lg shadow-xl max-h-60 overflow-y-auto"
                  >
                    {addressSuggestions.map((suggestion, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleSelectAddress(suggestion)}
                        className="w-full px-4 py-3 text-left hover:bg-violet-500/10 border-b border-white/5 last:border-0 transition-colors"
                      >
                        <p className="text-white text-sm font-medium truncate">{suggestion.street || suggestion.address}</p>
                        <p className="text-gray-400 text-xs truncate">{suggestion.city}, {suggestion.province}</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">{t('explore.city')} *</Label>
                  <Input
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder={t('explore.city')}
                    className="mt-1 bg-[#15121e] border-white/10 text-white"
                    required
                  />
                </div>
                <div>
                  <Label className="text-gray-300">{t('addSpot.addressConfirmed')}</Label>
                  <Input
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder={t('addSpot.addressConfirmed')}
                    className="mt-1 bg-[#15121e] border-white/10 text-white"
                    required
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Precios */}
          <section className="bg-[#231f30] rounded-xl p-6 border border-white/5">
            <h2 className="text-lg font-semibold text-white mb-4">{t('addSpot.pricing')}</h2>
            
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-gray-300">{t('addSpot.priceHour')}</Label>
                <Input
                  name="price_hour"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.price_hour}
                  onChange={handleChange}
                  placeholder="2.50"
                  className="mt-1 bg-[#15121e] border-white/10 text-white"
                  required
                />
              </div>
              <div>
                <Label className="text-gray-300">{t('addSpot.priceDay')}</Label>
                <Input
                  name="price_day"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.price_day}
                  onChange={handleChange}
                  placeholder="15.00"
                  className="mt-1 bg-[#15121e] border-white/10 text-white"
                />
              </div>
              <div>
                <Label className="text-gray-300">{t('addSpot.priceMonth')}</Label>
                <Input
                  name="price_month"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.price_month}
                  onChange={handleChange}
                  placeholder="120.00"
                  className="mt-1 bg-[#15121e] border-white/10 text-white"
                />
              </div>
            </div>
          </section>

          {/* Requisitos */}
          <section className="bg-[#231f30] rounded-xl p-6 border border-white/5">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <IdCard className="w-5 h-5 text-violet-400" />
              {t('addSpot.bookingRequirements')}
            </h2>
            
            <div className="flex items-center justify-between p-4 bg-[#15121e] rounded-lg border border-white/10">
              <div>
                <p className="text-white font-medium">{t('addSpot.requireDni')}</p>
                <p className="text-gray-400 text-sm">{t('addSpot.requireDniDesc')}</p>
              </div>
              <Switch
                checked={formData.requires_dni}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, requires_dni: checked }))}
              />
            </div>
          </section>

          {/* Plazafy Gate */}
          <section className="bg-gradient-to-r from-violet-500/10 to-emerald-500/10 rounded-xl p-6 border border-violet-500/20">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-violet-400" />
              Plazafy Gate
              <span className="ml-2 px-2 py-0.5 bg-violet-500/20 text-violet-400 text-xs rounded-full">
                Nuevo
              </span>
            </h2>
            
            <div className="flex items-center justify-between p-4 bg-[#15121e] rounded-lg border border-white/10">
              <div>
                <p className="text-white font-medium">¿Tienes Plazafy Gate instalado?</p>
                <p className="text-gray-400 text-sm">
                  Marca esta opción si tu plaza tiene el dispositivo de control de acceso inteligente.
                  <a href="/plazafy-gate" target="_blank" rel="noopener noreferrer" className="text-violet-400 hover:text-violet-300 ml-1">
                    Más info →
                  </a>
                </p>
              </div>
              <Switch
                checked={formData.has_plazafy_gate}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, has_plazafy_gate: checked }))}
                data-testid="plazafy-gate-switch"
              />
            </div>
          </section>

          {/* Características */}
          <section className="bg-[#231f30] rounded-xl p-6 border border-white/5">
            <h2 className="text-lg font-semibold text-white mb-4">{t('addSpot.features')}</h2>
            
            <div className="flex flex-wrap gap-2">
              {FEATURE_OPTIONS.map((feature) => (
                <button
                  key={feature}
                  type="button"
                  onClick={() => handleFeatureToggle(feature)}
                  className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                    formData.features.includes(feature)
                      ? "bg-violet-500 text-white"
                      : "bg-white/5 text-gray-400 hover:bg-white/10"
                  }`}
                >
                  {feature}
                </button>
              ))}
            </div>
          </section>

          {/* Tipos de vehículo */}
          <section className="bg-[#231f30] rounded-xl p-6 border border-white/5">
            <h2 className="text-lg font-semibold text-white mb-4">{t('addSpot.vehicleTypes')}</h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {VEHICLE_TYPES.map((type) => (
                <button
                  key={type.id}
                  type="button"
                  onClick={() => handleVehicleTypeToggle(type.id)}
                  className={`p-3 rounded-lg text-sm transition-all border ${
                    formData.vehicle_types.includes(type.id)
                      ? "bg-violet-500/20 border-violet-500 text-white"
                      : "bg-white/5 border-white/10 text-gray-400 hover:border-white/20"
                  }`}
                >
                  {t(type.labelKey) || type.id}
                </button>
              ))}
            </div>
          </section>

          {/* Imágenes */}
          <section className="bg-[#231f30] rounded-xl p-6 border border-white/5">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Camera className="w-5 h-5 text-violet-400" />
              {t('addSpot.photos')}
            </h2>
            
            {/* Upload area */}
            <div 
              className="border-2 border-dashed border-white/20 hover:border-violet-500/50 rounded-xl p-8 text-center cursor-pointer transition-all hover:bg-violet-500/5"
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageUpload}
                accept="image/*"
                multiple
                className="hidden"
              />
              
              {uploadingImage ? (
                <div className="flex flex-col items-center">
                  <Loader2 className="w-10 h-10 text-violet-400 animate-spin mb-3" />
                  <p className="text-gray-400">{t('addSpot.uploadingImage')}</p>
                </div>
              ) : (
                <div className="flex flex-col items-center">
                  <Upload className="w-10 h-10 text-gray-500 mb-3" />
                  <p className="text-white font-medium mb-1">{t('addSpot.dragPhotos')}</p>
                  <p className="text-gray-400 text-sm mb-3">{t('addSpot.orClick')}</p>
                  <p className="text-gray-500 text-xs">{t('addSpot.maxSize')}</p>
                </div>
              )}
            </div>

            {/* Preview de imágenes */}
            {formData.images.length > 0 && (
              <div className="mt-4">
                <p className="text-gray-400 text-sm mb-3">{t('addSpot.photosAdded', { count: formData.images.length })}</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {formData.images.map((img, index) => (
                    <div key={index} className="relative group aspect-video">
                      <img
                        src={img}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-full object-cover rounded-lg"
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeImage(img);
                          }}
                          className="p-2 bg-red-500 hover:bg-red-600 rounded-full transition-colors"
                        >
                          <X className="w-4 h-4 text-white" />
                        </button>
                      </div>
                      {index === 0 && (
                        <span className="absolute top-2 left-2 px-2 py-0.5 bg-violet-500 text-white text-xs rounded-full">
                          {t('addSpot.mainPhoto')}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>

          {/* Aceptación de términos */}
          <section className="bg-[#231f30] rounded-xl p-6 border border-white/5">
            <div className="flex items-start gap-3">
              <button
                type="button"
                onClick={() => setAcceptedTerms(!acceptedTerms)}
                className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded border transition-all flex items-center justify-center ${
                  acceptedTerms 
                    ? "bg-violet-600 border-violet-600" 
                    : "border-white/20 bg-transparent hover:border-white/40"
                }`}
                data-testid="addspot-terms-checkbox"
              >
                {acceptedTerms && <Check className="w-3.5 h-3.5 text-white" />}
              </button>
              <p className="text-gray-400 text-sm leading-snug">
                Al publicar esta plaza, confirmo que soy el propietario legítimo o tengo autorización legal para alquilarla, 
                y acepto los{" "}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-violet-400 hover:text-violet-300 underline">
                  Términos y Condiciones
                </a>{" "}
                y la{" "}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-violet-400 hover:text-violet-300 underline">
                  Política de Privacidad
                </a>
              </p>
            </div>
          </section>

          {/* Submit */}
          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 bg-violet-500 hover:bg-violet-600 text-white font-medium"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                {t('addSpot.publishing')}
              </>
            ) : (
              t('addSpot.publish')
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
